import React from "react";
import { FormattedRelativeTime } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery, useSubscription } from "@apollo/client";
import InfiniteScroll from "react-infinite-scroll-component";

import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tooltip,
  Box,
} from "@mui/material";

import Layout from "components/common/Layout";
import OrderEvent from "components/order/OrderEvent";

// Poor man's deep clone, we're using it only for graphql data,
// in this case it's ok to use JSON serialize/deserialize to clone
// object.
const deepClone = (val) => JSON.parse(JSON.stringify(val));

const QUERY = gql`
  query ($orderId: ID!, $first: Int, $after: String) {
    order(id: $orderId) {
      id
      events(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }

        edges {
          cursor
          node {
            id
            createdAt
            pinned

            ...Orders_OrderEvent_OrderEvent
          }
        }
      }
    }
  }

  ${OrderEvent.fragments.orderEvent}
`;

const SUBSCRIPTION = gql`
  subscription ($orderId: ID!) {
    orderEvents(orderId: $orderId) {
      id
      createdAt
      pinned

      ...Orders_OrderEvent_OrderEvent
    }
  }

  ${OrderEvent.fragments.orderEvent}
`;

function sortEdges(edgeA, edgeB) {
  if (edgeA.node.pinned === edgeB.node.pinned) {
    if (edgeA.node.createdAt < edgeB.node.createdAt) return 1;
    if (edgeA.node.createdAt > edgeB.node.createdAt) return -1;
    return 0;
  }

  return edgeA.node.pinned ? -1 : 1;
}

function updateOrderEvents(client, orderId, order) {
  client.writeFragment({
    id: `Order:${orderId}`,
    fragmentName: "UpdateOrderEvents",
    fragment: gql`
      fragment UpdateOrderEvents on Order {
        id
        events {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              id
              createdAt
              pinned

              ...Orders_OrderEvent_OrderEvent
            }
          }
        }
      }

      ${OrderEvent.fragments.orderEvent}
    `,
    data: order,
  });
}

export default function OrderTimeline({ orderId }) {
  const { data, loading, fetchMore } = useQuery(QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: { orderId, first: 20 },
  });

  useSubscription(SUBSCRIPTION, {
    variables: { orderId },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const orderEvent = subscriptionData.data.orderEvents;

      const orderEvents = deepClone(data?.order?.events);
      orderEvents.edges = orderEvents.edges.filter(
        (edge) => edge.node.id !== orderEvent.id
      );

      orderEvents.edges.unshift({
        cursor: orderEvent.id,
        node: orderEvent,
        __typename: "OrderEventEdge",
      });

      orderEvents.edges = orderEvents.edges.sort(sortEdges);

      const lastEdge = orderEvents.edges[orderEvents.edges.length - 1];
      const isLastEdge = lastEdge.node.id === orderEvent.id;

      if (isLastEdge && orderEvents.pageInfo.hasNextPage) {
        orderEvents.edges.pop();
      }

      updateOrderEvents(client, orderId, {
        ...data.order,
        events: orderEvents,
      });
    },
  });

  return (
    <InfiniteScroll
      scrollableTarget="Layout-Main"
      style={{ overflow: "visible" }}
      dataLength={data?.order?.events.edges.length ?? 0}
      next={() =>
        fetchMore({
          variables: {
            after: data?.order?.events.pageInfo.endCursor,
          },
        })
      }
      loader={<Layout.Loading />}
      hasMore={data?.order?.events.pageInfo.hasNextPage ?? loading}
      hasChildren
    >
      <Stepper
        activeStep={0}
        orientation="vertical"
        nonLinear
        sx={{ pl: 1, pt: 2 }}
      >
        {data?.order?.events.edges.map(({ cursor, node }) => (
          <Step key={cursor} active>
            <StepLabel
              sx={{ ml: -1, p: 0 }}
              icon={
                <Tooltip
                  placement="left"
                  title={
                    <FormattedRelativeTime
                      value={
                        (new Date(node.createdAt).getTime() - Date.now()) / 1000
                      }
                      numeric="auto"
                      updateIntervalInSeconds={1}
                    />
                  }
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      color: "text.secondary",
                      bgcolor: (t) => t.palette.background.paper,
                      boxShadow: (t) => t.shadows[2],
                    }}
                  >
                    <OrderEvent.Icon orderEvent={node} />
                  </Box>
                </Tooltip>
              }
            />
            <StepContent sx={{ pr: 0 }}>
              <OrderEvent
                sx={{ ml: 2, mt: "-56px", mb: 1 }}
                orderEvent={node}
              />
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </InfiniteScroll>
  );
}
